import React, { useContext, useState } from 'react';
import { MdLockReset } from 'react-icons/md';
import { AuthContext } from '../context/AuthContext';
import API from '../api/axios';
import './ChangePasswordPrompt.css';

/**
 * Shown after login while the user is still on an admin-assigned password
 * (user.mustChangePassword, e.g. the default 123456). The user can set their
 * own password right here or choose "Later" — which hides the prompt for this
 * session only; it returns on the next login until the password is changed.
 */
export default function ChangePasswordPrompt() {
  const { user, updateUser } = useContext(AuthContext);
  const [later, setLater] = useState(false);
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!user?.mustChangePassword || later) return null;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.currentPassword || !form.newPassword) {
      setError('Enter your current password and a new password.');
      return;
    }
    if (form.newPassword.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }
    if (form.newPassword === form.currentPassword) {
      setError('Choose a password different from your current one.');
      return;
    }
    try {
      setSaving(true);
      await API.put('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword
      });
      updateUser({ mustChangePassword: false });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cpp-overlay" role="dialog" aria-modal="true" aria-labelledby="cpp-title">
      <form className="cpp-card" onSubmit={handleSubmit}>
        <div className="cpp-head">
          <MdLockReset aria-hidden="true" />
          <div>
            <h3 id="cpp-title">Set your own password</h3>
            <p>
              You are signed in with the password given by your administrator. For your
              account's security, change it now.
            </p>
          </div>
        </div>

        <label className="form-group">
          <span className="form-label">Current password</span>
          <input className="form-input" type="password" autoComplete="current-password" value={form.currentPassword} onChange={set('currentPassword')} placeholder="The password you just used to log in" />
        </label>
        <label className="form-group">
          <span className="form-label">New password</span>
          <input className="form-input" type="password" autoComplete="new-password" value={form.newPassword} onChange={set('newPassword')} placeholder="At least 6 characters" />
        </label>
        <label className="form-group">
          <span className="form-label">Confirm new password</span>
          <input className="form-input" type="password" autoComplete="new-password" value={form.confirmPassword} onChange={set('confirmPassword')} />
        </label>

        {error && <p className="cpp-error" role="alert">{error}</p>}

        <div className="cpp-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setLater(true)} disabled={saving}>
            Later
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
}
